import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  Loader2, Check, X, BookOpen, Rocket, Users, GraduationCap, Calendar, IndianRupee,
  ArrowRight, MessageCircle, ShoppingBag,
} from "lucide-react";
import { motion } from "framer-motion";

// Map exact age → marketplace age band
function ageToBand(age) {
  const a = Number(age);
  if (a >= 2 && a <= 4) return "2-4";
  if (a >= 5 && a <= 7) return "5-7";
  if (a >= 8 && a <= 10) return "8-10";
  if (a >= 11 && a <= 13) return "11-13";
  if (a >= 14 && a <= 16) return "14-16";
  if (a >= 17 && a <= 18) return "17-18";
  return null;
}

function Gauge({ value, label, color = "#059669" }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  const circ = 2 * Math.PI * 46;
  return (
    <div className="flex flex-col items-center">
      <svg width="140" height="140" viewBox="0 0 120 120" className="-rotate-90">
        <circle cx="60" cy="60" r="46" stroke="#F3F4F6" strokeWidth="10" fill="none" />
        <motion.circle
          cx="60" cy="60" r="46" stroke={color} strokeWidth="10" fill="none" strokeLinecap="round"
          strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: circ - (circ * pct) / 100 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />
      </svg>
      <div className="-mt-24 text-center">
        <div className="text-4xl font-bold font-heading text-brand-blue">{pct}</div>
        <div className="text-xs text-brand-muted">/ 100</div>
      </div>
      <div className="mt-14 text-sm font-medium text-brand-blue">{label}</div>
    </div>
  );
}

export default function Report() {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/assessment/${id}`)
      .then((r) => setReport(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Report not found"));
  }, [id]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="text-2xl text-brand-blue">{error}</h1>
        <Link to="/assessment" className="btn-primary mt-6">Start assessment</Link>
      </div>
    );
  }
  if (!report) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <Loader2 className="animate-spin text-brand-green mx-auto" size={40} />
        <p className="mt-4 text-brand-muted">Loading your report...</p>
      </div>
    );
  }

  const child = report.input?.child_name || "your child";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      {/* Header */}
      <div className="mb-10">
        <div className="overline mb-3">Your personalized report</div>
        <h1 data-testid="report-heading" className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-blue leading-tight">
          A learning journey for <span className="text-brand-green">{child}</span>.
        </h1>
      </div>

      {/* Score cards */}
      <div className="grid md:grid-cols-3 gap-6 mb-10">
        <div className="surface p-8 md:col-span-1 flex flex-col items-center">
          <Gauge value={report.suitability_score} label="Homeschool suitability" color="#059669" />
        </div>
        <div className="surface p-8 md:col-span-1 flex flex-col items-center">
          <Gauge value={report.parent_readiness_score} label="Parent readiness" color="#F58A4C" />
        </div>
        <div className="surface p-8 md:col-span-1 bg-gradient-to-br from-brand-blue to-brand-blue-hover text-white">
          <div className="text-sm text-white/70 uppercase tracking-widest mb-3">Recommended paths</div>
          <div className="space-y-2">
            {(report.recommended_paths || []).map((p) => (
              <div key={p} data-testid={`report-path-${p.toLowerCase().replace(/\s+/g,'-')}`} className="flex items-center gap-2 font-heading text-lg">
                <ArrowRight size={16} className="text-brand-orange" /> {p}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="surface p-8 mb-10">
        <div className="chip mb-3">Summary</div>
        <p data-testid="report-summary" className="text-lg text-brand-blue leading-relaxed">{report.summary}</p>
      </div>

      {/* Pros & Cons */}
      <div className="grid md:grid-cols-2 gap-6 mb-10">
        <div className="surface p-7">
          <div className="chip mb-4">Pros for {child}</div>
          <ul className="space-y-2.5">
            {(report.pros || []).map((p, i) => (
              <li key={i} className="flex gap-3"><Check size={18} className="text-brand-green shrink-0 mt-0.5" /><span className="text-brand-blue">{p}</span></li>
            ))}
          </ul>
        </div>
        <div className="surface p-7">
          <div className="chip-orange mb-4">Watch-outs</div>
          <ul className="space-y-2.5">
            {(report.cons || []).map((p, i) => (
              <li key={i} className="flex gap-3"><X size={18} className="text-brand-orange shrink-0 mt-0.5" /><span className="text-brand-blue">{p}</span></li>
            ))}
          </ul>
        </div>
      </div>

      {/* Curricula & Budget */}
      <div className="grid md:grid-cols-3 gap-6 mb-10">
        <div className="surface p-7 md:col-span-2">
          <div className="flex items-center gap-2 mb-4"><BookOpen size={20} className="text-brand-green" /><h3 className="text-xl font-semibold text-brand-blue">Recommended curricula</h3></div>
          <div className="flex flex-wrap gap-2">
            {(report.recommended_curricula || []).map((c) => (
              <Link key={c} to="/curriculum" className="chip-blue hover:bg-brand-blue hover:text-white transition-colors">{c}</Link>
            ))}
          </div>
        </div>
        <div className="surface p-7">
          <div className="flex items-center gap-2 mb-2"><IndianRupee size={18} className="text-brand-orange" /><h3 className="font-semibold text-brand-blue">Budget estimate</h3></div>
          <p className="text-brand-blue font-heading text-lg">{report.budget_estimate}</p>
        </div>
      </div>

      {/* Weekly schedule */}
      {report.weekly_schedule?.length > 0 && (
        <div className="surface p-7 mb-10 overflow-x-auto">
          <div className="flex items-center gap-2 mb-4"><Calendar size={20} className="text-brand-green" /><h3 className="text-xl font-semibold text-brand-blue">Suggested weekly schedule</h3></div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-brand-muted">
                <th className="py-2 pr-4">Day</th>
                <th className="py-2 pr-4">Morning</th>
                <th className="py-2 pr-4">Afternoon</th>
                <th className="py-2">Evening</th>
              </tr>
            </thead>
            <tbody>
              {report.weekly_schedule.map((d, i) => (
                <tr key={i} className="border-t border-brand-border">
                  <td className="py-3 pr-4 font-medium text-brand-blue">{d.day}</td>
                  <td className="py-3 pr-4 text-brand-muted">{d.morning}</td>
                  <td className="py-3 pr-4 text-brand-muted">{d.afternoon}</td>
                  <td className="py-3 text-brand-muted">{d.evening}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Roadmap */}
      {report.roadmap?.length > 0 && (
        <div className="surface p-7 mb-10">
          <h3 className="text-xl font-semibold text-brand-blue mb-6">Long-term roadmap</h3>
          <div className="grid md:grid-cols-4 gap-4">
            {report.roadmap.map((r, i) => (
              <div key={i} className="p-5 rounded-2xl bg-brand-bg border border-brand-border">
                <div className="text-xs uppercase tracking-wider text-brand-green font-bold mb-2">{r.phase}</div>
                <div className="text-brand-blue leading-relaxed text-sm">{r.focus}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Careers */}
      {report.career_recommendations?.length > 0 && (
        <div className="surface p-7 mb-10">
          <div className="flex items-center gap-2 mb-4"><Rocket size={20} className="text-brand-orange" /><h3 className="text-xl font-semibold text-brand-blue">Future careers to explore</h3></div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {report.career_recommendations.map((c, i) => (
              <div key={i} className="p-5 rounded-2xl bg-brand-bg border border-brand-border">
                <div className="font-semibold text-brand-blue">{c.name}</div>
                <div className="text-sm text-brand-muted mt-1">{c.why}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Communities & Tutors */}
      <div className="grid md:grid-cols-2 gap-6 mb-10">
        <div className="surface p-7">
          <div className="flex items-center gap-2 mb-4"><Users size={20} className="text-brand-green" /><h3 className="text-xl font-semibold text-brand-blue">Communities to consider</h3></div>
          <ul className="space-y-2 text-brand-muted">{(report.community_recommendations || []).map((c, i) => <li key={i}>• {c}</li>)}</ul>
          <Link to="/communities" className="btn-outline mt-5">Browse communities</Link>
        </div>
        <div className="surface p-7">
          <div className="flex items-center gap-2 mb-4"><GraduationCap size={20} className="text-brand-blue" /><h3 className="text-xl font-semibold text-brand-blue">Tutor recommendations</h3></div>
          <ul className="space-y-2 text-brand-muted">{(report.tutor_recommendations || []).map((c, i) => <li key={i}>• {c}</li>)}</ul>
          <Link to="/tutors" className="btn-outline mt-5">Find tutors</Link>
        </div>
      </div>

      {/* CTA */}
      <div className="rounded-3xl p-8 lg:p-12 text-center bg-gradient-to-br from-brand-green to-emerald-700 text-white">
        <MessageCircle size={32} className="mx-auto mb-3" />
        <h3 className="text-2xl font-semibold">Questions about this report?</h3>
        <p className="mt-2 text-white/80">Talk to our AI Parent Assistant — it knows your report.</p>
        <Link to="/assistant" className="btn-primary mt-5 inline-flex">Open AI Assistant</Link>
      </div>
    </div>
  );
}
